import { observer } from 'mobx-react';
import { useMemo } from 'react';
import { formatNum, formatPercent } from 'app/utils';
import { LeadNowConvVM } from 'app/stores/ui/agt.leadnow.conv.ui.store';

export interface IFunnelTotals {
	brutto: number;
	netto: number;
	converted: number;
	neuStck: number;
}

interface ILeadNowConversionFunnelChart {
	items: LeadNowConvVM[];
	// When set, overlay a "Durchschnitt" reference line built from this baseline
	// population (typically all GP-relevant agts). Omit it when the displayed
	// funnel already represents the average (i.e. when viewing all agts).
	averageItems?: LeadNowConvVM[];
	// Pre-aggregated benchmark totals for the "Durchschnitt" line (e.g. a VD or
	// ABV average from the benchmark endpoint). Takes precedence over averageItems.
	averageTotals?: IFunnelTotals;
	// Label floated above the average line. Defaults to "Durchschnitt".
	averageLabel?: string;
	// Optional second benchmark line (e.g. the VD average next to the ABV average).
	// Rendered in its own colour with the label on the left-hand side of the graph.
	secondAverageTotals?: IFunnelTotals;
	secondAverageLabel?: string;
	// Extra class on the root, e.g. 'is-widget' to apply the compact dashboard styling.
	modifier?: string;
	// Override the chart height in px (viewBox units). Defaults to VB_HEIGHT (200).
	height?: number;
}

const sumTotals = (items: LeadNowConvVM[]): IFunnelTotals =>
	items.reduce(
		(acc, i) => {
			acc.brutto += i.brutto;
			acc.netto += i.netto;
			acc.converted += i.converted;
			acc.neuStck += i.neuStck;
			return acc;
		},
		{ brutto: 0, netto: 0, converted: 0, neuStck: 0 },
	);

// viewBox geometry. preserveAspectRatio="none" lets the bars stretch to the
// container width while keeping the vertical scale 1:1 (viewBox unit === px).
const VB_WIDTH = 1000;
const VB_HEIGHT = 200;
const TOP_HEADROOM = 20; // px kept free above the tallest bar
const GAP_PCT = 0.8; // white gap between bars; MUST match $funnel-gap in leadnow.scss
const COL_PCT = (100 - GAP_PCT * 3) / 4; // width of a single column, in %

const pctStr = (a: number, b: number, dec: number) => `${formatPercent(b ? (a / b) * 100 : 0, dec)}%`;

interface IFunnelStage {
	key: 'brutto' | 'netto' | 'konvertiert' | 'stuecke';
	label: string;
	value: number;
	sub: string;
}

/**
 * Lead-Conversion funnel. Each bar is bottom-aligned and its value-height is
 * linear to the largest stage. The top edge of a bar flows from its own value
 * (left edge) to the next stage's value (right edge) via a bezier with
 * horizontal tangents at both column edges - this makes the whole top a single
 * continuous wave across all four bars.
 */
export const LeadNowConversionFunnelChart = observer((props: ILeadNowConversionFunnelChart) => {
	const totals = useMemo(() => sumTotals(props.items), [props.items]);
	const avgTotals = useMemo(() => {
		if (props.averageTotals) {
			return props.averageTotals;
		}
		return props.averageItems && props.averageItems.length > 0 ? sumTotals(props.averageItems) : undefined;
	}, [props.averageTotals, props.averageItems]);

	const vbHeight = props.height ?? VB_HEIGHT;
	const baseline = vbHeight;
	const barMaxHeight = vbHeight - TOP_HEADROOM; // tallest (max value) bar; leaves headroom on top

	const { brutto, netto, converted, neuStck } = totals;
	const stkProLead = converted ? neuStck / converted : 0;

	const stages: IFunnelStage[] = [
		{
			key: 'brutto',
			label: 'Brutto Leads',
			value: brutto,
			sub: `${pctStr(brutto, netto, 0)} (${formatNum(brutto)} / ${formatNum(netto)})`,
		},
		{
			key: 'netto',
			label: 'Netto Leads',
			value: netto,
			sub: `${pctStr(netto, netto, 0)} (${formatNum(netto)} / ${formatNum(netto)})`,
		},
		{
			key: 'konvertiert',
			label: 'Konvertierte Leads',
			value: converted,
			sub: `${pctStr(converted, netto, 1)} (${formatNum(converted)} / ${formatNum(netto)})`,
		},
		{
			key: 'stuecke',
			label: 'Stücke',
			value: neuStck,
			sub: `${formatPercent(stkProLead, 1)} pro Lead`,
		},
	];

	const maxValue = Math.max(...stages.map((s) => s.value), 1);
	const heightOf = (value: number) => (value / maxValue) * barMaxHeight;

	// column x-geometry in viewBox units, derived from the same % model the
	// HTML grid (labels/callouts) uses, so bars and labels stay aligned.
	const colWidthVb = (COL_PCT / 100) * VB_WIDTH;
	const stepVb = ((COL_PCT + GAP_PCT) / 100) * VB_WIDTH;

	const bars = stages.map((stage, i) => {
		const x0 = i * stepVb;
		const x1 = x0 + colWidthVb;
		const mx = (x0 + x1) / 2;
		const hL = heightOf(stage.value);
		const hR = i < stages.length - 1 ? heightOf(stages[i + 1].value) : hL;
		const yL = baseline - hL;
		const yR = baseline - hR;
		const d = `M ${x0} ${baseline} L ${x0} ${yL} C ${mx} ${yL} ${mx} ${yR} ${x1} ${yR} L ${x1} ${baseline} Z`;
		return { d, key: stage.key };
	});

	// Overlay wave: the same bezier shape as the bar tops, but for a benchmark
	// funnel scaled to its own brutto. Scaling by brutto/refBrutto puts both
	// funnels in the same %-of-brutto space, so the line reads purely as a
	// conversion-shape comparison (above the bars = below-average conversion).
	// leftY/rightY (first/last stage, in viewBox units) double as px offsets for
	// HTML labels floated above the line, since the svg's pixel height === VB_HEIGHT.
	const buildWave = (t: IFunnelTotals) => {
		if (!t.brutto) return undefined;
		const scale = brutto / t.brutto;
		const values = [t.brutto, t.netto, t.converted, t.neuStck];
		const yOf = (i: number) => baseline - heightOf(values[i] * scale);

		let d = `M 0 ${yOf(0)}`;
		values.forEach((_, i) => {
			const x0 = i * stepVb;
			const x1 = x0 + colWidthVb;
			const mx = (x0 + x1) / 2;
			const yL = yOf(i);
			const yR = i < values.length - 1 ? yOf(i + 1) : yL;
			d += ` C ${mx} ${yL} ${mx} ${yR} ${x1} ${yR}`;
			if (i < values.length - 1) {
				// horizontal carry across the gap; next column's left top == this yR
				d += ` L ${(i + 1) * stepVb} ${yR}`;
			}
		});
		return { d, leftY: yOf(0), rightY: yOf(values.length - 1) };
	};

	// The primary "Durchschnitt" overlay (label right), from averageTotals or
	// averageItems, plus the optional second overlay (label left).
	const avgLine = avgTotals ? buildWave(avgTotals) : undefined;
	const secondAvgLine = props.secondAverageTotals ? buildWave(props.secondAverageTotals) : undefined;
	const avgStkProLead = avgTotals && avgTotals.converted ? avgTotals.neuStck / avgTotals.converted : 0;
	// "ABV Durchschnitt" -> "ABV Ø", plain "Durchschnitt" -> "Ø"
	const avgLabel = props.averageLabel ?? 'Durchschnitt';
	const avgPrefix = `${avgLabel.replace('Durchschnitt', '').trim()} Ø`.trim();

	return (
		<div className={`leadnow-conversion-funnel pad-1rem ${props.modifier ?? ''}`}>
			{/* floating callout boxes, placed over their column via a grid overlay */}
			<div className="funnel-callouts">
				<div className="funnel-callout is-konvertiert">
					<div className="callout-title">Abschlussquote</div>
					<div className="callout-value">
						{pctStr(converted, netto, 1)}
						{avgTotals && (
							<span className="callout-avg" title={avgLabel}>
								{avgPrefix} {pctStr(avgTotals.converted, avgTotals.netto, 1)}
							</span>
						)}
					</div>
				</div>
				<div className="funnel-callout is-stuecke">
					<div className="callout-title">Stücke pro Lead</div>
					<div className="callout-value">
						{formatPercent(stkProLead, 1)}
						{avgTotals && (
							<span className="callout-avg" title={avgLabel}>
								{avgPrefix} {formatPercent(avgStkProLead, 1)}
							</span>
						)}
					</div>
				</div>
			</div>

			<div className="funnel-chart-area">
				<svg className="funnel-bars" height={vbHeight} viewBox={`0 0 ${VB_WIDTH} ${vbHeight}`} preserveAspectRatio="none">
					{bars.map((bar) => (
						<path key={bar.key} className={`funnel-bar is-${bar.key}`} d={bar.d} />
					))}
					{avgLine && <path className="funnel-avg-line" d={avgLine.d} vectorEffect="non-scaling-stroke" />}
					{secondAvgLine && <path className="funnel-avg-line is-second" d={secondAvgLine.d} vectorEffect="non-scaling-stroke" />}
				</svg>
				{avgLine && (
					<div className="funnel-avg-label" style={{ bottom: `${vbHeight - avgLine.rightY}px` }}>
						{props.averageLabel ?? 'Durchschnitt'}
					</div>
				)}
				{secondAvgLine && (
					<div className="funnel-avg-label is-second" style={{ bottom: `${vbHeight - secondAvgLine.leftY}px` }}>
						{props.secondAverageLabel ?? 'Durchschnitt'}
					</div>
				)}
			</div>

			{/* stage labels, aligned column-for-column with the bars above */}
			<div className="funnel-labels">
				{stages.map((stage) => (
					<div key={stage.key} className={`funnel-label is-${stage.key}`}>
						<div className="label-value">{formatNum(stage.value)}</div>
						<div className="label-title">{stage.label}</div>
						<div className="label-sub">{stage.sub}</div>
					</div>
				))}
			</div>
		</div>
	);
});
